import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  Home as HomeIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import {
  isGridNumericCellValue,
  isTextIdentifierField,
  parseGridNumericCellValue,
} from '../../../utils/gridCellValueUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateUploadOutlinedSx,
} from '../../../theme/harmonateStyles';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

const LandingInternalCasRefPage = ({ 
  onBackToWorkflows, 
  onBackToReferenceDrawer, 
  selectedQuarter, 
  selectedReferenceData,
  selectedFile,
  onBackToReferenceFiles
}) => {
  // State for Landing Internal CAS Ref data
  const [landingInternalCasRefData, setLandingInternalCasRefData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [gridColumns, setGridColumns] = useState([]);
  const [gridData, setGridData] = useState([]);
  


  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();

  const fetchWorkflowDefinition = async (documentTypeId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-definition-snowflake/document-type/${documentTypeId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch workflow definition:', response.status, errorText);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching workflow definition:', error);
    return null;
  }
};

  // Fetch Landing Internal CAS Ref data
 const fetchLandingInternalCasRefData = async () => {
  if (!selectedFile?.integrationId || !selectedFile?.workflowId || !selectedFile?.documentTypeId) {
    console.log('Missing required data for Landing Internal CAS Ref:', {
      integrationId: selectedFile?.integrationId,
      workflowId: selectedFile?.workflowId,
      documentTypeId: selectedFile?.documentTypeId
    });
    showError('Missing required file information. Please ensure the file has a valid workflow instance ID and document type ID.');
    return;
  }

  // Log reference file view
  try {
    const { AuditLoggers } = await import('../../../utils/auditLogger');
    await AuditLoggers.referenceFileViewed(
      selectedFile.id,
      selectedFile.name,
      {
        integrationId: selectedFile.integrationId,
        workflowId: selectedFile.workflowId,
        documentTypeId: selectedFile.documentTypeId
      }
    );
  } catch (error) {
    console.error('Error logging reference file view:', error);
    // Continue even if logging fails
  }

  console.log('Starting Landing Internal CAS Ref fetch...');
  setLoading(true);
  setError(null);

  try {
    // First, fetch the workflow definition to get the snowflake table name
    const workflowDef = await fetchWorkflowDefinition(selectedFile.documentTypeId);
    // Use the snowflakeTableName from the API response, default to 'EXTERNAL_FS_SOI_REF' if not found
    const tableName = workflowDef?.[0]?.snowflakeTableName;
    console.log('Using landing table name:', tableName);

    const token = localStorage.getItem('token');
    let url = `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/landing-ref?file_id=${selectedFile.integrationId}&workflow_id=${selectedFile.workflowId}&table_name=${tableName}`;
    
    // Add tenant_id parameter if available
    const tenantId = localStorage.getItem('tenant_integration');
    if (tenantId) {
      url += `&tenant_id=${tenantId}`;
    }
    
    console.log('Landing Internal CAS Ref URL:', url);
    console.log('API Parameters:', {
      file_id: selectedFile.integrationId,
      workflow_id: selectedFile.workflowId,
      table_name: tableName,
      tenant_id: tenantId
    });
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Landing Internal CAS Ref response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Landing Internal CAS Ref error response:', errorText);
        throw new Error(`Failed to fetch landing internal CAS ref data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Landing Internal CAS Ref data received:', data);
      setLandingInternalCasRefData(data);
      
      // Process data for grid display
      processDataForGrid(data);
      
             const recordCount = data.count || (Array.isArray(data) ? data.length : 1);
       showSuccess(`Successfully loaded ${recordCount} records`);
    } catch (err) {
      console.error('Error fetching landing internal CAS ref data:', err);
      setError(err.message);
      showError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Process the JSON data to create grid columns and rows from CONTENT only
  const processDataForGrid = (data) => {
    if (!data || !data.data || data.data.length === 0) {
      setGridColumns([]);
      setGridData([]);
      return;
    }

    // Extract all CONTENT objects from the data array
    const contentObjects = data.data
      .filter(item => item.CONTENT && typeof item.CONTENT === 'object')
      .map(item => item.CONTENT);

    if (contentObjects.length === 0) {
      setGridColumns([]);
      setGridData([]);
      return;
    }

    // Get all unique keys from all CONTENT objects to create columns
    const allKeys = new Set();
    contentObjects.forEach(content => {
      Object.keys(content).forEach(key => allKeys.add(key));
    });

    // Create columns from the CONTENT keys
    const columns = Array.from(allKeys).map((key) => ({
      field: key,
      headerName: formatHeaderName(key),
      width: 200,
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        
        // Format plain numeric cells only (not investor names like "2020 Revocable Test One, LP")
        if (!isTextIdentifierField(key) && isGridNumericCellValue(value)) {
          const numericValue = parseGridNumericCellValue(value);
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                fontFamily: 'monospace',
                color: numericValue < 0 ? 'error.main' : numericValue > 0 ? 'success.main' : 'text.primary',
                fontWeight: numericValue !== 0 ? 500 : 400,
                textAlign: 'right'
              }}
            >
              {numericValue.toLocaleString()}
            </Typography>
          );
        }
        
        // Identifier / name columns — always show full text
        if (isTextIdentifierField(key)) {
          return (
            <Typography variant="body2" sx={{ maxWidth: '100%' }}>
              {String(value)}
            </Typography>
          );
        }
        
        // Handle date fields
        if (key.toLowerCase().includes('date') || key.toLowerCase().includes('period')) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              return (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {date.toLocaleDateString()}
                </Typography>
              );
            }
          } catch (e) {
            // If date parsing fails, fall back to string
          }
        }
        
        return (
          <Typography variant="body2" sx={{ maxWidth: '100%' }}>
            {String(value)}
          </Typography>
        );
      }
    }));

    setGridColumns(columns);

    // Prepare grid data with unique IDs - each row is a CONTENT object
    const gridData = contentObjects.map((content, index) => ({ 
      ...content, 
      id: index + 1 
    }));
    
    setGridData(gridData);
  };

  // Format header names to be more readable
  const formatHeaderName = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handle retry
  const handleRetry = () => {
    fetchLandingInternalCasRefData();
  };

  // Fetch data when component mounts
  useEffect(() => {
    if (selectedFile) {
      fetchLandingInternalCasRefData();
    }
  }, [selectedFile]);

  return (
    <>
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              Workflows
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToReferenceDrawer}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <FolderOpenIcon sx={{ fontSize: '1rem' }} />
              Reference Files
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToReferenceFiles}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <DescriptionIcon sx={{ fontSize: '1rem' }} />
              {selectedReferenceData?.documentType || 'File List'}
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {selectedFile?.name || 'File'}
            </Typography>
          </Breadcrumbs>
        </Box>
        
        {/* Content */}
        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, overflow: 'hidden' }}>
          {/* Loading state */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress sx={{ color: ht.green }} />
              <Typography variant="body1" sx={{ ml: 2 }}>Loading Landing Internal CAS Ref data...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '400px', justifyContent: 'center' }}>
              <Alert severity="error" sx={{ mb: 2, maxWidth: '500px' }}>
                <Typography>Failed to load data: {error}</Typography>
              </Alert>
              <Button 
                variant="outlined" 
                onClick={handleRetry}
                sx={harmonateUploadOutlinedSx}
              >
                Retry
              </Button>
            </Box>
          ) : gridData.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '400px', justifyContent: 'center' }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                No Landing Internal CAS Ref data available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The selected file may not have any associated reference data.
              </Typography>
            </Box>
          ) : (
            <DataGridPro
              rows={gridData}
              columns={gridColumns}
              rowHeight={50}
              columnResizeMode="onEnd"
              resizable
              sx={{ 
                ...harmonateDataGridSx,
                height: 780,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.8rem',
                  py: 1,
                },
                '& .MuiDataGrid-columnHeader': {
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
              }}
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              getRowId={(row) => row.id}
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    No data available
                  </Typography>
                </Box>
              )}
              components={{
                LoadingOverlay: LinearProgress,
              }}
            />
          )}
        </Paper>
      </Box>

      

       {/* Common Toast Component */}
       <Toast 
         open={toastState.open}
         onClose={hideToast}
         message={toastState.message}
         severity={toastState.severity}
         title={toastState.title}
         autoHideDuration={toastState.autoHideDuration}
         position={toastState.position}
         variant={toastState.variant}
       />
     </>
   );
 };

export default LandingInternalCasRefPage;
